import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
  align-items: center;
  gap: 1rem;

  label {
    font-size: 14px;
    margin-right: 8px;
    // color: ${({ colors }) => colors.labelText};
    color: "#000";
    font-weight: bold;
  }

  select,
  input {
    padding: 5px 10px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    align-items: stretch;

    select,
    input {
      width: 100%;
      box-sizing: border-box;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 10px;
  min-width: 900px;
  width: 100%;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 14px;

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
    padding: 8px 16px;
    position: relative;
    font-size: 13px;

    .sort-icons {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      font-size: 10px;

      span {
        cursor: pointer;
        line-height: 10px;
        color: #9CA3AF;
        display: block;
      }

      .active {
        color: #A855F7;
      }
    }
  }

  tbody td {
    ${adminTableDataCell};
    padding: 8px 16px;
    font-size: 13px;
  }

  .userCell {
    white-space: pre-line;
  }
`;

const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  gap: 8px;

  button {
    padding: 5px 10px;
    border: 1px solid ${({ colors }) => colors.border};
    background-color: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;
    font-size: 13px;

    &:disabled {
      background-color: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }
    &:hover:not(:disabled) {
      background-color: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }

  @media (max-width: ${breakpoints.phone}px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const PageContainer = styled.div`
  width: 100%;
`;

export default function ReportDownloadLogContent({ colors }) {
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [loading, setLoading] = useState(true);

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(null);     
  const [sortOrder, setSortOrder] = useState(null);

  // Fetch from API whenever page or pageSize changes
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const resp = await API.getReportLogs({
          page: currentPage - 1,
          items_per_page: pageSize,
        });
        setLogs(resp.report_logs || []);
        setTotalLogs(Number(resp.total || 0));
      } catch (err) {
        console.error("Failed to load report logs:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentPage, pageSize]);

  const filtered = useMemo(() => {
    if (!searchTerm) return logs;
    const term = searchTerm.toLowerCase();
    return logs.filter((r) =>
      [
        r.org_name,
        r.report_part,
        r.name,
        r.ip_address,
        r.inserted,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [logs, searchTerm]);

  // 2) Sort client-side on the filtered page
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = (a[sortKey] || "").toLowerCase();
      const bVal = (b[sortKey] || "").toLowerCase();
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortOrder]);

  const pageCount = Math.ceil(totalLogs / pageSize);

  const handleSort = (key, order) => {
    setSortKey(key);
    setSortOrder(order);
    setCurrentPage(1);
  };

  return (
    <PageContainer>
      <ControlsContainer colors={colors}>
        <div>
          <label>Show</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label> entries</label>
        </div>
        <div>
          <label>Search:</label>
          <input
            type="text"
            style={{ width: 300 }}
            value={searchTerm}
            placeholder="Filter current page…"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </ControlsContainer>

      <TableWrapper>
        <Table colors={colors}>
          <thead>
            <tr>
              {[
                { key: "org_name", label: "Organization" },
                { key: "report_part", label: "Type" },
                { key: "name", label: "User" },
                { key: "inserted", label: "Date / Time" },
              ].map(({ key, label }) => {
                const isAscActive = sortKey === key && sortOrder === "asc";
                const isDescActive = sortKey === key && sortOrder === "desc";
                const defaultColor = "#9CA3AF";
                const activeColor = "#A855F7";
                return (
                  <th key={key}>
                    {label}
                    <div className="sort-icons">
                      <span
                        onClick={() => handleSort(key, "asc")}
                        className={isAscActive ? "active" : ""}
                        style={{
                          color: isAscActive ? activeColor : defaultColor,
                          display: isDescActive ? "none" : "block"
                        }}
                      >
                        ▲
                      </span>
                      <span
                        onClick={() => handleSort(key, "desc")}
                        className={isDescActive ? "active" : ""}
                        style={{
                          color: isDescActive ? activeColor : defaultColor,
                          display: isAscActive ? "none" : "block"
                        }}
                      >
                        ▼
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                  Loading Logs...
                </td>
              </tr>
            ) : (
              sorted.map((r) => (
                <tr key={r.log_id}>
                  <td>{r.org_name}</td>
                  <td>{r.report_part}</td>
                  <td className="userCell">
                    {r.name}
                    <br />
                    <small>({r.ip_address})</small>
                  </td>
                  <td>{r.inserted}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>

      <InfoText colors={colors}>
        Showing{" "}
        {logs.length === 0
          ? 0
          : (currentPage - 1) * pageSize + 1}{" "}
        to{" "}
        {Math.min(currentPage * pageSize, totalLogs)}{" "}
        of {totalLogs} entries
      </InfoText>

      <Pagination colors={colors}>
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, pageCount))}
          disabled={currentPage === pageCount}
        >
          Next
        </button>
      </Pagination>
    </PageContainer>
  );
}
